/**
 * mediaService — photos, songs and videos attached to a puzzle (clue
 * celebrations and the reveal; videos only on the reveal), stored in Cloud Storage. The puzzle doc keeps only the URL.
 *
 * Why Storage and not the doc: celebration media used to be embedded as base64.
 * Photos fit once compressed, but songs did not — Firestore caps a document at
 * 1 MiB, so a 1 MB song was rejected (`INVALID_ARGUMENT`) and a 4 MB one hung
 * until the write timed out. Checked against the emulator on 2026-09-22.
 *
 * Uploads go to puzzles/{puzzleId}/media/{random}.{ext}. The puzzle id is chosen
 * before the puzzle exists (the creator generates it when it starts) so media
 * picked mid-flow already sits under the id the puzzle will be published with.
 * storage.rules only lets a signed-in user create files there — never overwrite.
 *
 * Firebase Storage is imported lazily, like reactionService: most sessions never
 * attach media and shouldn't pay for the SDK.
 */
import { requireContentRulesAccepted } from "./contentRules";
import logger from "./logger";

// Limits mirror storage.rules. Checked here too, so the user gets a clear
// message before a doomed upload instead of a Storage permission error after it.
export const MAX_PHOTO_BYTES = 2 * 1024 * 1024;
export const MAX_SONG_BYTES = 8 * 1024 * 1024;
// A short clip from the camera roll. Not re-encoded (no reliable way to do it in
// the browser), so the cap is on the file as picked: ~30-60 s of phone video.
export const MAX_VIDEO_BYTES = 50 * 1024 * 1024;

// Photos are re-encoded before upload: the reveal photo is the payoff, so it is
// kept sharper than the old 800 px celebration photos, and still lands well under
// the 2 MB cap in practice.
const PHOTO_MAX_EDGE = 1600;
const PHOTO_QUALITY = 0.85;

const PUZZLE_ID_RE = /^[A-Za-z0-9]{12}$/;

/** A puzzle media error with a stable `code` the UI can translate. */
export class MediaError extends Error {
  /** @param {"too-large"|"wrong-type"|"bad-puzzle-id"|"upload-failed"} code */
  constructor(code, message) {
    super(message || code);
    this.name = "MediaError";
    this.code = code;
  }
}

/**
 * True for URLs that point at our Storage. Used to decide what the player is
 * willing to render: a legacy link carries its whole config client-side and could
 * otherwise name any src. The emulator host is accepted so local runs work.
 */
export function isStorageUrl(url) {
  if (typeof url !== "string") return false;
  return (
    url.startsWith("https://firebasestorage.googleapis.com/") ||
    url.startsWith("http://127.0.0.1:9199/") ||
    url.startsWith("http://localhost:9199/")
  );
}

function randomName() {
  const bytes = crypto.getRandomValues(new Uint8Array(9));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

function extensionFor(type, fallback) {
  const sub = (type || "").split("/")[1] || "";
  const clean = sub.split(";")[0].replace(/[^a-z0-9]/gi, "").toLowerCase();
  if (clean === "mpeg") return "mp3";
  if (clean === "jpeg") return "jpg";
  if (clean === "quicktime") return "mov";
  return clean || fallback;
}

/**
 * Re-encode an image as JPEG, longest edge capped at PHOTO_MAX_EDGE.
 * @param {Blob} file
 * @returns {Promise<Blob>}
 */
export async function compressPhoto(file, maxEdge = PHOTO_MAX_EDGE, quality = PHOTO_QUALITY) {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  canvas.getContext("2d").drawImage(bitmap, 0, 0, width, height);
  if (typeof bitmap.close === "function") bitmap.close();
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new MediaError("upload-failed", "could not encode photo"))),
      "image/jpeg",
      quality
    );
  });
}

/**
 * Validate and prepare a file for upload. Pure except for photo compression,
 * which is injectable so tests don't need a canvas.
 * @returns {Promise<{ blob: Blob, contentType: string, ext: string }>}
 */
export async function prepareMedia(file, kind, { compress = compressPhoto } = {}) {
  if (!file || typeof file.size !== "number") throw new MediaError("wrong-type");
  const type = file.type || "";

  if (kind === "photo") {
    if (!type.startsWith("image/")) throw new MediaError("wrong-type");
    let blob = await compress(file);
    // One more, harder pass for the rare photo that is still over the cap.
    if (blob.size >= MAX_PHOTO_BYTES) blob = await compress(file, 1200, 0.7);
    if (blob.size >= MAX_PHOTO_BYTES) throw new MediaError("too-large");
    return { blob, contentType: "image/jpeg", ext: "jpg" };
  }

  if (kind === "song") {
    if (!type.startsWith("audio/")) throw new MediaError("wrong-type");
    if (file.size >= MAX_SONG_BYTES) throw new MediaError("too-large");
    return { blob: file, contentType: type, ext: extensionFor(type, "mp3") };
  }

  if (kind === "video") {
    if (!type.startsWith("video/")) throw new MediaError("wrong-type");
    if (file.size >= MAX_VIDEO_BYTES) throw new MediaError("too-large");
    return { blob: file, contentType: type, ext: extensionFor(type, "mp4") };
  }

  throw new MediaError("wrong-type", `unknown media kind: ${kind}`);
}

/**
 * Upload a photo, song or video for a puzzle and return its download URL.
 * @param {{ puzzleId: string, file: Blob, kind: "photo"|"song"|"video" }} args
 * @returns {Promise<string>}
 */
export async function uploadPuzzleMedia({ puzzleId, file, kind }, deps = {}) {
  if (!PUZZLE_ID_RE.test(puzzleId || "")) throw new MediaError("bad-puzzle-id");
  // Prima del primo file che parte, le regole accettate (#65).
  await (deps.requireRules || requireContentRulesAccepted)({ profile: deps.profile });
  const { blob, contentType, ext } = await prepareMedia(file, kind, deps);

  const [{ storage }, { ref, uploadBytes, getDownloadURL }] = await Promise.all([
    deps.firebase ? Promise.resolve(deps.firebase) : import("./firebase"),
    deps.storageSdk ? Promise.resolve(deps.storageSdk) : import("firebase/storage"),
  ]);

  const path = `puzzles/${puzzleId}/media/${randomName()}.${ext}`;
  try {
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, blob, { contentType });
    const url = await getDownloadURL(storageRef);
    logger.info("puzzle media uploaded", { puzzleId, kind, bytes: blob.size });
    return url;
  } catch (err) {
    logger.error("puzzle media upload failed", { puzzleId, kind, error: err && err.message, code: err && err.code });
    throw new MediaError("upload-failed", err && err.message);
  }
}
